import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft, Send, Headphones, CheckCheck, Loader2, Sparkles, Phone, CreditCard, Bot, Paperclip, FileText, Download, X, Lock, Unlock, Hourglass,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// SUPPORT TECKIA : chat direct entre le client et l'équipe (gratuit : Appwrite + serveur TeckiA).
// - actualisation automatique toutes les 3 secondes tant que le chat est ouvert
// - pièces jointes : photos, PDF, documents, notes vocales, vidéos courtes
// - le client choisit la langue du support : Français ou Malagasy
// - toutes les réponses de l'équipe s'affichent sous le nom « Support TeckiA »
// ============================================================

const authHeaders = (json = true) => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}`, ...(json ? { 'Content-Type': 'application/json' } : {}) });
const POLL_MS = 3000;
const SUPPORT_NAME = 'Support TeckiA';
const ACCEPT = 'image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip';

const T = {
  fr: {
    sub: "Une vraie personne de l'équipe vous répond",
    welcome: "Bonjour 👋 Bienvenue au support TeckiA. Écrivez-nous votre question ou votre besoin : nous vous répondons ici, et vous recevez une notification dans l'application dès qu'il y a une réponse.",
    faq: 'Questions fréquentes',
    quick: ["J'aimerais qu'on m'appelle pour configurer mon agent.", 'Mon agent ne répond pas, pouvez-vous vérifier ?', "J'ai une question sur mon paiement / mon abonnement."],
    placeholder: 'Écrivez votre message…', hint: 'Entrée pour envoyer · Maj + Entrée pour aller à la ligne',
    today: "Aujourd'hui", yesterday: 'Hier', seen: "Lu par l'équipe", sent: 'Envoyé', attach: 'Joindre une photo ou un fichier', uploading: 'Envoi du fichier…',
    chooseTitle: 'Choisissez la langue du support', chooseSub: "L'équipe vous répondra dans cette langue. Vous pourrez la changer à tout moment.",
    open: 'Ouvrir', download: 'Télécharger', locale: 'fr-FR', fileErr: 'Fichier non envoyé.',
    closedTitle: 'Conversation fermée', closedText: "L'équipe a clôturé cette conversation. Si vous avez encore besoin d'aide, demandez à rediscuter : l'équipe sera prévenue et la rouvrira.",
    reopenBtn: 'Demander à discuter avec le support', reopenWaitTitle: 'Demande envoyée ✅', reopenWaitText: "L'équipe TeckiA a reçu votre demande. Vous serez prévenu dans votre messagerie dès que la conversation sera rouverte.",
  },
  mg: {
    sub: "Olona tena izy ao amin'ny ekipa no mamaly anao",
    welcome: "Salama 👋 Tonga soa eto amin'ny Support TeckiA. Soraty eto ny fanontanianao na ny zavatra ilainao: hamaly anao eto izahay, ary hahazo fampandrenesana ao amin'ny application ianao raha vao misy valiny.",
    faq: 'Fanontaniana mahazatra',
    quick: ["Tiako ny hiantsoanareo ahy mba hanamboatra ny agent-ko.", 'Tsy mamaly ny agent-ko, azonao jerena ve?', "Manana fanontaniana momba ny fandoavako vola / ny abonnement-ko aho."],
    placeholder: 'Soraty ny hafatrao…', hint: 'Tsindrio Entrée raha handefa · Maj + Entrée raha hidina andalana',
    today: 'Androany', yesterday: 'Omaly', seen: "Hitan'ny ekipa", sent: 'Voalefa', attach: 'Hampiditra sary na rakitra', uploading: 'Mandefa ny rakitra…',
    chooseTitle: 'Safidio ny fiteny', chooseSub: "Hamaly anao amin'io fiteny io ny ekipa. Azonao ovaina na oviana na oviana.",
    open: 'Sokafy', download: 'Alaina', locale: 'fr-FR', fileErr: 'Tsy lasa ny rakitra.',
    closedTitle: 'Voatakatona ny resaka', closedText: "Nakatona ity resaka ity ny ekipa. Raha mbola mila fanampiana ianao, angataho hiresaka indray: hampandrenesina ny ekipa ary hosokafany indray.",
    reopenBtn: "Angataho hiresaka amin'ny Support", reopenWaitTitle: 'Voalefa ny fangatahana ✅', reopenWaitText: "Voaray ny fangatahanao ao amin'ny ekipa TeckiA. Hampandrenesina ianao ao amin'ny hafatra raha vao misokatra indray ny resaka.",
  },
};
const QUICK_ICONS = [Phone, Bot, CreditCard];

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);
const hhmm = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

// Télécharge une pièce jointe protégée (en-tête d'authentification) et la rend sous forme d'URL locale
export function useAuthBlob(id, enabled = true) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true; let obj = '';
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/support/files/${id}`, { headers: authHeaders(false) });
        if (!r.ok) return;
        obj = URL.createObjectURL(await r.blob());
        if (alive) setUrl(obj);
      } catch (_) { /* fichier indisponible */ }
    })();
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [id, enabled]);
  return url;
}

export async function saveAttachment(id, name) {
  const r = await fetch(`${API_URL}/api/support/files/${id}?dl=1`, { headers: authHeaders(false) });
  if (!r.ok) return;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Affichage d'une pièce jointe dans une bulle (photo, audio, vidéo, fichier)
export function AttachmentView({ m, labels = T.fr, onImage }) {
  const a = m.attachment;
  const real = !String(m.id).startsWith('tmp-');
  const url = useAuthBlob(m.id, real && (a.kind === 'image' || a.kind === 'audio' || a.kind === 'video'));
  if (a.kind === 'image') {
    return url
      ? <img src={url} alt={a.name} onClick={() => onImage && onImage(url, a.name)} className="rounded-xl max-h-64 max-w-full object-cover cursor-zoom-in mb-1.5" />
      : <div className="w-48 h-32 rounded-xl bg-white/10 flex items-center justify-center mb-1.5"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (a.kind === 'audio') return <audio controls src={url || undefined} className="max-w-full mb-1.5" style={{ height: 40 }} />;
  if (a.kind === 'video') return <video controls src={url || undefined} className="rounded-xl max-h-64 max-w-full mb-1.5" />;
  return (
    <button onClick={() => real && saveAttachment(m.id, a.name)} className="flex items-center gap-3 w-full text-left rounded-xl bg-black/20 hover:bg-black/30 px-3 py-2.5 mb-1.5 transition">
      <span className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0"><FileText className="w-5 h-5" /></span>
      <span className="min-w-0 flex-1"><span className="block text-sm font-semibold truncate">{a.name}</span><span className="block text-[11px] opacity-70">{fmtSize(a.size)}</span></span>
      <Download className="w-4 h-4 opacity-80 shrink-0" title={labels.download} />
    </button>
  );
}

// pastille « Support » pour l'en-tête du tableau de bord : nombre de réponses non lues
export function SupportButton({ onClick }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/support/unread`, { headers: authHeaders() });
        const d = await r.json();
        if (alive && d.success) setUnread(d.unread || 0);
      } catch (_) { /* hors ligne : on garde l'ancien compteur */ }
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 5000);     // badge quasi temps réel (compteur Redis côté serveur)
    const vis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', vis);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, []);
  return (
    <button onClick={onClick} title="Écrire au support TeckiA"
      className={`relative flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl text-white font-semibold transition-all hover:scale-105 text-sm md:text-base ${unread > 0
        ? 'bg-red-600 hover:bg-red-500 shadow-lg shadow-red-600/40'
        : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 shadow-lg shadow-orange-600/30'}`}>
      <Headphones className={`w-4 h-4 ${unread > 0 ? 'animate-pulse' : ''}`} />
      <span className="hidden sm:inline">Support</span>
      {unread > 0 && <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-white text-red-600 border-2 border-red-600 text-[11px] font-extrabold flex items-center justify-center shadow-lg">{unread > 99 ? '99+' : unread}</span>}
    </button>
  );
}

const CSS = `
.sc-bubble { position:relative; max-width:min(82%,520px); padding:10px 14px 8px; border-radius:20px; font-size:14.5px; line-height:1.5; white-space:pre-wrap; word-wrap:break-word; overflow-wrap:anywhere; box-shadow:0 10px 24px rgba(0,0,0,.28); animation:scIn .25s ease both; }
.sc-me { align-self:flex-end; color:#fff; border-bottom-right-radius:6px; background:linear-gradient(145deg,#7c3aed,#4f46e5); box-shadow:0 10px 24px rgba(79,70,229,.35), inset 0 1px 0 rgba(255,255,255,.25); }
.sc-team { align-self:flex-start; color:#eef2ff; border-bottom-left-radius:6px; background:linear-gradient(145deg,rgba(255,255,255,.13),rgba(255,255,255,.05)); border:1px solid rgba(255,255,255,.14); backdrop-filter:blur(10px); }
@keyframes scIn { from { opacity:0; transform:translateY(8px) scale(.98); } to { opacity:1; transform:none; } }
.sc-send { width:46px; height:46px; border-radius:16px; display:flex; align-items:center; justify-content:center; color:#fff; border:0; cursor:pointer; flex:none; transition:transform .12s, box-shadow .12s;
  background:linear-gradient(145deg,#8b5cf6,#4f46e5); box-shadow:0 5px 0 #3730a3, 0 14px 24px rgba(79,70,229,.4); }
.sc-send:active { transform:translateY(3px); box-shadow:0 2px 0 #3730a3, 0 8px 14px rgba(79,70,229,.3); }
.sc-send:disabled { opacity:.45; cursor:not-allowed; box-shadow:0 3px 0 #3730a3; }
.sc-lang { display:inline-flex; padding:3px; border-radius:12px; background:rgba(255,255,255,.1); }
.sc-lang button { padding:5px 11px; border-radius:9px; font-size:12px; font-weight:800; color:rgba(255,255,255,.65); transition:all .15s; }
.sc-lang button.on { background:linear-gradient(145deg,#8b5cf6,#4f46e5); color:#fff; box-shadow:0 4px 10px rgba(79,70,229,.45); }
.sc-choice { transition:transform .2s, box-shadow .2s; transform:perspective(800px) rotateX(2deg); box-shadow:0 20px 40px rgba(0,0,0,.4); }
.sc-choice:hover { transform:perspective(800px) rotateX(0) translateY(-4px); }
.sc-scroll::-webkit-scrollbar { width:6px; } .sc-scroll::-webkit-scrollbar-thumb { background:rgba(255,255,255,.18); border-radius:6px; }
@media (prefers-reduced-motion: reduce) { .sc-bubble, .sc-choice { animation:none; transition:none; } }
`;

export default function SupportChat({ onBack }) {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('');           // '' = pas encore choisie
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [seenAt, setSeenAt] = useState('');
  const [status, setStatus] = useState('open');     // open | closed | reopen (demande de réouverture envoyée)
  const [asking, setAsking] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const endRef = useRef(null);
  const boxRef = useRef(null);
  const fileRef = useRef(null);
  const lastRef = useRef('');
  const stickRef = useRef(true);
  const L = T[lang] || T.fr;

  const merge = useCallback((incoming) => {
    if (!incoming.length) return;
    setMsgs(prev => {
      const ids = new Set(prev.map(m => m.id));
      const add = incoming.filter(m => !ids.has(m.id));
      const base = prev.filter(m => !(m.tmp && add.some(a => a.sender === 'user' && a.body === m.body && !!a.attachment === !!m.attachment)));
      return add.length || base.length !== prev.length ? [...base, ...add] : prev;
    });
    const last = incoming[incoming.length - 1].created_at;
    if (last > lastRef.current) lastRef.current = last;
  }, []);

  const load = useCallback(async (first) => {
    try {
      const url = `${API_URL}/api/support/messages${!first && lastRef.current ? `?after=${encodeURIComponent(lastRef.current)}` : ''}`;
      const r = await fetch(url, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) {
        if (first) {
          setMsgs(d.messages); lastRef.current = d.messages.length ? d.messages[d.messages.length - 1].created_at : '';
          setLang(d.lang || (d.messages.length ? 'fr' : ''));   // une conversation déjà commencée sans choix = français
        } else merge(d.messages);
        setSeenAt(d.admin_seen_at || '');
        setStatus(d.status || 'open');
      }
    } catch (_) { /* coupure passagère : on réessaie au prochain tour */ } finally { if (first) setLoading(false); }
  }, [merge]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => { if (!document.hidden) load(false); }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (stickRef.current) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs, loading, lang]);
  const onScroll = () => { const el = boxRef.current; if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120; };

  const chooseLang = async (l) => {
    setLang(l);
    try { await fetch(`${API_URL}/api/support/lang`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ lang: l }) }); } catch (_) { /* sera réenregistrée au prochain message */ }
  };

  const send = async (override) => {
    const body = (override ?? text).trim();
    if (!body || sending) return;
    setSending(true); setError('');
    const tmp = { id: `tmp-${Date.now()}`, sender: 'user', body, created_at: new Date().toISOString(), tmp: true };
    setMsgs(m => [...m, tmp]); setText(''); stickRef.current = true;
    try {
      const r = await fetch(`${API_URL}/api/support/messages`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ body }) });
      const d = await r.json();
      if (d.success) merge([d.message]);
      else { setMsgs(m => m.filter(x => x.id !== tmp.id)); setText(body); if (d.closed) { setStatus(d.status || 'closed'); setError(''); } else setError(d.message || 'Message non envoyé.'); }
    } catch (_) {
      setMsgs(m => m.filter(x => x.id !== tmp.id)); setText(body); setError('Connexion impossible. Réessayez.');
    } finally { setSending(false); }
  };

  const upload = async (file) => {
    if (!file || uploading) return;
    setUploading(true); setError('');
    const fd = new FormData(); fd.append('file', file); fd.append('body', text.trim());
    stickRef.current = true;
    try {
      const r = await fetch(`${API_URL}/api/support/attachments`, { method: 'POST', headers: authHeaders(false), body: fd });
      const d = await r.json();
      if (d.success) { setText(''); merge([d.message]); }
      else if (d.closed) { setStatus(d.status || 'closed'); setError(''); }
      else setError(d.message || L.fileErr);
    } catch (_) { setError('Connexion impossible. Réessayez.'); } finally { setUploading(false); }
  };

  const askReopen = async () => {
    if (asking) return;
    setAsking(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/support/reopen`, { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (d.success) { setStatus(d.status || 'reopen'); stickRef.current = true; load(false); }
      else setError(d.message || 'Demande non envoyée.');
    } catch (_) { setError('Connexion impossible. Réessayez.'); } finally { setAsking(false); }
  };

  const onPick = (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); };
  const onPaste = (e) => {
    const f = [...(e.clipboardData?.files || [])][0];
    if (f) { e.preventDefault(); upload(f); }
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const dayLabel = (iso) => {
    const d = new Date(iso); const t = new Date(); const y = new Date(Date.now() - 86400000);
    if (d.toDateString() === t.toDateString()) return L.today;
    if (d.toDateString() === y.toDateString()) return L.yesterday;
    return d.toLocaleDateString(L.locale, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  let lastDay = '';
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white">
      <style>{CSS}</style>

      {/* en-tête */}
      <div className="flex-shrink-0 border-b border-white/10 bg-black/35 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={onBack} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition"><ArrowLeft className="w-5 h-5" /></button>
          <div className="relative w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-900/50">
            <Headphones className="w-5 h-5" />
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-slate-950" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold leading-tight">{SUPPORT_NAME}</div>
            <div className="text-xs text-indigo-200/80 truncate">{L.sub}</div>
          </div>
          {lang && (
            <div className="sc-lang" title="Langue / Fiteny">
              <button className={lang === 'fr' ? 'on' : ''} onClick={() => lang !== 'fr' && chooseLang('fr')}>FR</button>
              <button className={lang === 'mg' ? 'on' : ''} onClick={() => lang !== 'mg' && chooseLang('mg')}>MG</button>
            </div>
          )}
        </div>
      </div>

      {/* choix de la langue (première ouverture) */}
      {!loading && !lang ? (
        <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 mx-auto rounded-3xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-2xl mb-4"><Headphones className="w-8 h-8" /></div>
            <h2 className="text-xl font-extrabold">Choisissez la langue du support</h2>
            <p className="text-lg font-bold text-indigo-200 mt-0.5">Safidio ny fiteny</p>
            <p className="text-sm text-indigo-100/70 mt-2">L'équipe vous répondra dans cette langue. · Hamaly anao amin'io fiteny io ny ekipa.</p>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <button onClick={() => chooseLang('fr')} className="sc-choice rounded-3xl p-5 bg-gradient-to-br from-white/[0.12] to-white/[0.03] border border-white/15">
                <div className="text-4xl mb-2">🇫🇷</div><div className="font-bold">Français</div>
              </button>
              <button onClick={() => chooseLang('mg')} className="sc-choice rounded-3xl p-5 bg-gradient-to-br from-white/[0.12] to-white/[0.03] border border-white/15">
                <div className="text-4xl mb-2">🇲🇬</div><div className="font-bold">Malagasy</div>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div ref={boxRef} onScroll={onScroll} className="sc-scroll flex-1 min-h-0 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-5 flex flex-col gap-2.5">
              <div className="sc-bubble sc-team">
                <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-200 mb-1"><Sparkles className="w-3.5 h-3.5" /> {SUPPORT_NAME}</div>
                {L.welcome}
              </div>

              {loading ? (
                <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-300" /></div>
              ) : msgs.map(m => {
                const day = dayLabel(m.created_at);
                const showDay = day !== lastDay; lastDay = day;
                const mine = m.sender === 'user';
                const seen = mine && !m.tmp && seenAt && seenAt >= m.created_at;
                return (
                  <React.Fragment key={m.id}>
                    {showDay && <div className="self-center my-1 px-3 py-1 rounded-full bg-white/10 text-[11px] text-indigo-100/80 capitalize">{day}</div>}
                    <div className={`sc-bubble ${mine ? 'sc-me' : 'sc-team'} ${m.tmp ? 'opacity-70' : ''}`}>
                      {!mine && <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-200 mb-1"><Headphones className="w-3.5 h-3.5" /> {SUPPORT_NAME}</div>}
                      {m.attachment && <AttachmentView m={m} labels={L} onImage={(u, n) => setLightbox({ url: u, name: n })} />}
                      {m.body}
                      <div className={`mt-1 flex items-center justify-end gap-1 text-[10.5px] ${mine ? 'text-indigo-100/75' : 'text-indigo-200/60'}`}>
                        {hhmm(m.created_at)}
                        {mine && (m.tmp ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className={`w-3.5 h-3.5 ${seen ? 'text-sky-300' : ''}`} title={seen ? L.seen : L.sent} />)}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {uploading && <div className="sc-bubble sc-me opacity-80 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {L.uploading}</div>}

              {!loading && msgs.length === 0 && (
                <div className="mt-2 grid gap-2">
                  <div className="text-xs font-bold tracking-widest uppercase text-white/45 text-center mb-1">{L.faq}</div>
                  {L.quick.map((q, i) => { const Ic = QUICK_ICONS[i]; return (
                    <button key={q} onClick={() => send(q)}
                      className="text-left flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.06] hover:bg-white/[0.12] border border-white/12 transition text-sm shadow-lg">
                      <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/50 to-indigo-500/50 flex items-center justify-center shrink-0"><Ic className="w-4 h-4" /></span>
                      {q}
                    </button>
                  ); })}
                </div>
              )}
              <div ref={endRef} />
            </div>
          </div>

          {/* conversation fermée par l'équipe : plus d'envoi, bouton pour demander à rediscuter */}
          {status !== 'open' ? (
            <div className="flex-shrink-0 border-t border-white/10 bg-black/40 backdrop-blur-xl">
              <div className="max-w-3xl mx-auto px-4 py-4">
                <div className="rounded-3xl p-4 sm:p-5 border border-white/15 bg-gradient-to-br from-white/[0.10] to-white/[0.03] shadow-2xl flex flex-col sm:flex-row sm:items-center gap-4">
                  <span className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-lg ${status === 'reopen' ? 'bg-gradient-to-br from-emerald-400 to-teal-600' : 'bg-gradient-to-br from-slate-500 to-slate-700'}`}>
                    {status === 'reopen' ? <Hourglass className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{status === 'reopen' ? L.reopenWaitTitle : L.closedTitle}</div>
                    <p className="text-sm text-indigo-100/75 mt-0.5">{status === 'reopen' ? L.reopenWaitText : L.closedText}</p>
                    {error && <p className="text-sm text-rose-300 mt-1">{error}</p>}
                  </div>
                  {status === 'closed' && (
                    <button onClick={askReopen} disabled={asking}
                      className="shrink-0 px-5 py-3 rounded-2xl font-bold text-sm bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 shadow-lg shadow-orange-900/40 transition hover:scale-[1.03] disabled:opacity-60 flex items-center justify-center gap-2">
                      {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} {L.reopenBtn}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
          <div className="flex-shrink-0 border-t border-white/10 bg-black/40 backdrop-blur-xl">
            <div className="max-w-3xl mx-auto px-4 py-3">
              {error && <p className="text-sm text-rose-300 mb-2">{error}</p>}
              <div className="flex items-end gap-2.5">
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onPick} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} title={L.attach}
                  className="w-[46px] h-[46px] rounded-2xl bg-white/10 hover:bg-white/20 border border-white/12 flex items-center justify-center shrink-0 transition disabled:opacity-50"><Paperclip className="w-5 h-5" /></button>
                <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={onKey} onPaste={onPaste} rows={1} maxLength={2000} placeholder={L.placeholder}
                  style={{ maxHeight: 140, minHeight: 46 }}
                  onInput={e => { e.target.style.height = 'auto'; e.target.style.height = `${Math.min(140, e.target.scrollHeight)}px`; }}
                  className="flex-1 resize-none rounded-2xl bg-white/[0.08] border border-white/15 focus:border-violet-400/70 px-4 py-3 text-sm text-white placeholder-gray-400 outline-none" />
                <button className="sc-send" onClick={() => send()} disabled={!text.trim() || sending} title="Envoyer">
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-[11px] text-white/35 mt-1.5 text-center">{L.hint}</p>
            </div>
          </div>
          )}
        </>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/15" onClick={() => setLightbox(null)}><X className="w-5 h-5" /></button>
          <img src={lightbox.url} alt={lightbox.name} className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}

